'use client';

import { c as _c } from "react/compiler-runtime";
import { createTransactionTraceId, type SdkWallet, useActiveWallet, useActiveWalletSlot, useClassifyProposal, useTurnkeyModalActive, useTurnkeySigningDeps, useTurnkeySubmissionDeps, useWallets } from '@morfi/backend-sdk-v2/client';
import { usdcToD6 } from '@morfi/contract-sdk';
import { useTranslations } from 'next-intl';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import { CreateMarketProgressStrip } from '@/components/launchpad/create-market-progress-strip';
import { CreateProposalStepPreview } from '@/components/launchpad/create-proposal-step-preview';
import { CreateProposalStep3 } from '@/components/launchpad/create-proposal-step3';
import { ProposalCreatedSuccess } from '@/components/launchpad/proposal-created-success';
import { ProposalDetailsForm } from '@/components/launchpad/proposal-details-form';
import { ProposalFirstCreationPanel } from '@/components/launchpad/proposal-first-creation-panel';
import { ProposalStepNameEntry } from '@/components/launchpad/proposal-step-name-entry';
import { EventTimeCard, type EventTimeCardProps } from '@/components/live/event-time-card';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Alert01IconSolid, Tick02Icon } from '@/components/ui/icon/glyphs';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { ProgressDots } from '@/components/ui/progress-dots';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { StatusBanner } from '@/components/ui/status-banner';
import { walletDisplayLabel } from '@/components/wallet/wallet-display-label';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { useCreationFeeParams } from '@/hooks/launchpad/use-creation-fee-params';
import { useImageSearchPrefetch } from '@/hooks/launchpad/use-image-search-prefetch';
import type { CreateProposalResult } from '@/hooks/proposals/use-create-proposal';
import { useCreateProposalMutation } from '@/hooks/proposals/use-create-proposal';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useNotifyWalletSwitched } from '@/hooks/wallet/use-notify-wallet-switched';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { useFormattedCurrency } from '@/lib/currency';
import { type DynamicTranslator, resolveApiErrorMessage } from '@/lib/errors/resolve-api-error';
import { detectPredictionStyle } from '@/lib/launchpad/detect-prediction-style';
import { quoteFoundingFund } from '@/lib/launchpad/founding-fund-quote';
import { clearProposalAuthContinuation, createProposalAuthContinuation, type ProposalAuthContinuation, type ProposalAuthContinuationFailureReason, validateProposalAuthContinuationDraft, writeProposalAuthContinuation } from '@/lib/launchpad/proposal-auth-continuation';
import { useProposalGeneration } from '@/lib/sse/use-proposal-generation';
import { logger } from '@/lib/telemetry/logger';
import { getConnection } from '@/lib/tx-engine/connection';
import { useSubmitTransaction } from '@/lib/tx-engine/use-submit-transaction';
import { fireMarketCreatedBurst } from '@/lib/ui/confetti';
import { showToast } from '@/lib/ui/toast';
import { getExternalWalletProvider } from '@/lib/wallet/external-wallet-provider';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useInvalidateWalletBalances, useWalletBalances } from '@/queries/wallet-balance';
import { useProposalFirstCreationStore } from '@/stores/proposal-first-creation';
import { aiProposalFormStore, getDetailsStepErrors, manualProposalFormStore, type ProposalDraftSummary, ProposalFormStoreProvider, selectResumableDraft, useProposalFormStore } from '@/stores/proposal-form';
export interface LiveEventProposalContext extends Omit<EventTimeCardProps, 'variant'> {
  category: string;
}
export interface CreateProposalDialogFixtureState {
  step: 1 | 2 | 3 | 4;
  name: string;
  validation?: {
    kind: 'prediction' | 'duplicate';
    title: string;
    description: string;
    blocksProgress: boolean;
    action?: {
      label: string;
      href: string;
    };
  };
  draft: {
    title: string;
    marketTicker: string;
    description: string;
    location: string;
    coverImageUrl: string;
    outcomes: readonly {
      label: string;
      ticker: string;
      imageUrl: string;
    }[];
    fundAmount: string;
  };
}
interface CreateProposalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  continuation?: ProposalAuthContinuation | undefined;
  continuationFailure?: ProposalAuthContinuationFailureReason | undefined;
  liveEventContext?: LiveEventProposalContext | undefined;
  /** Developer-playground seed. Omitted by every production caller, preserving
   *  the ordinary persisted-draft, validation, auth, and submission flow. */
  fixtureState?: CreateProposalDialogFixtureState | undefined;
}

// Spec 113 — both creation methods share ONE 4-step flow:
//   1 = Enter market name, 2 = Market details, 3 = Preview, 4 = Fund.
const TOTAL_STEPS = 4;

// Lazy-load the tx engine out of the dialog's initial chunk. Kept as a
// module-level loader (not an inline `await import()` in the handler) so the
// React Compiler can memoize the component.
function loadBackMarketModule(): Promise<typeof import('@/lib/tx-engine/back-market')> {
  return import('@/lib/tx-engine/back-market');
}
function continuationElapsedMs(value: ProposalAuthContinuation): number {
  return Math.max(0, Date.now() - value.createdAt);
}
interface DraftSavedPillProps {
  onClearDraft: () => void;
}

/** Inline pill rendered in the footer, between Back and Next. */
function DraftSavedPill(t0) {
  const $ = _c(12);
  const {
    onClearDraft
  } = t0;
  const t = useTranslations("launchpad.form");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={Tick02Icon} className="size-3 text-feedback-success-content" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("draftSaved");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span aria-hidden="true">·</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("clearDraft");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onClearDraft || $[7] !== t4) {
    t5 = <button type="button" onClick={onClearDraft} className="rounded-item text-foreground-secondary underline-offset-2 transition-colors hover:text-foreground-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{t4}</button>;
    $[6] = onClearDraft;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t5) {
    t6 = <span className="flex items-center gap-1 text-caption text-foreground-tertiary">{t1}{t2}{t3}{t5}</span>;
    $[9] = t2;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

/**
 * Outer wrapper that owns the `flow` state and selects the per-flow draft
 * store via `<ProposalFormStoreProvider>`. Step 1 (name entry) always runs
 * with `flow === 'generate'` so the AI-generation hook is bound to the AI
 * store and "Generate with AI" can start streaming immediately; choosing
 * "Enter details manually" flips the flow and seeds the manual store
 * directly. Manual and Generate keep two separate localStorage drafts
 * (`morfi-proposal-form:manual` vs `:ai`).
 */
export function CreateProposalDialog(t0) {
  const $ = _c(15);
  const {
    open,
    onOpenChange,
    continuation,
    continuationFailure,
    liveEventContext,
    fixtureState
  } = t0;
  let t1;
  if ($[0] !== continuation?.flow || $[1] !== fixtureState) {
    t1 = () => fixtureState === undefined ? continuation?.flow ?? "generate" : "manual";
    $[0] = continuation?.flow;
    $[1] = fixtureState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [flow, setFlow] = useState(t1);
  const t2 = fixtureState !== undefined;
  let t3;
  if ($[3] !== continuation || $[4] !== continuationFailure || $[5] !== fixtureState || $[6] !== flow || $[7] !== liveEventContext || $[8] !== onOpenChange || $[9] !== open) {
    t3 = <CreateProposalDialogInner open={open} onOpenChange={onOpenChange} flow={flow} setFlow={setFlow} continuation={continuation} continuationFailure={continuationFailure} liveEventContext={liveEventContext} fixtureState={fixtureState} />;
    $[3] = continuation;
    $[4] = continuationFailure;
    $[5] = fixtureState;
    $[6] = flow;
    $[7] = liveEventContext;
    $[8] = onOpenChange;
    $[9] = open;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== flow || $[12] !== t2 || $[13] !== t3) {
    t4 = <ProposalFormStoreProvider flow={flow} ephemeral={t2}>{t3}</ProposalFormStoreProvider>;
    $[11] = flow;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
interface CreateProposalDialogInnerProps extends CreateProposalDialogProps {
  flow: 'manual' | 'generate';
  setFlow: (flow: 'manual' | 'generate') => void;
}
interface CreateProposalController {
  t: ReturnType<typeof useTranslations>;
  tAi: ReturnType<typeof useTranslations>;
  store: ReturnType<typeof useProposalFormStore.getState>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flow: 'manual' | 'generate';
  liveEventContext: LiveEventProposalContext | undefined;
  name: string;
  setName: Dispatch<SetStateAction<string>>;
  method: 'manual' | 'generate';
  setMethod: Dispatch<SetStateAction<'manual' | 'generate'>>;
  recentDraft: ProposalDraftSummary | null;
  locationEnabled: boolean;
  submitting: boolean;
  reviewTransitionPending: boolean;
  createdTitle: string | null;
  isAiStreaming: boolean;
  eligibleWallets: readonly SdkWallet[];
  selectedWalletId: string | null;
  walletBalancesD6: ReadonlyMap<string, bigint>;
  walletBalanceState: 'loading' | 'error' | 'ready';
  feeBpsBacking: number | undefined;
  feeBpsBuy: number | undefined;
  feeBpsSellNormal: number | undefined;
  feeSplitBackersBps: number | undefined;
  creationThresholdD6: string | undefined;
  configStatus: 'loading' | 'ready' | 'unavailable';
  canSubmit: boolean;
  stepTitles: Record<number, string>;
  stepDescriptions: Record<number, string>;
  hasDraft: boolean;
  hasDuplicateOutcomeLabel: boolean;
  showSuccess: boolean;
  showFirstCreationPanel: boolean;
  successPubkey: string | null;
  successDialogTitle: string;
  mutationProgress: ReturnType<typeof useCreateProposalMutation>['progress'];
  isAuthenticated: boolean;
  authResumePreparing: boolean;
  continuationFallbackVisible: boolean;
  continuationPersistenceLimited: boolean;
  fixtureValidation: CreateProposalDialogFixtureState['validation'];
  confirmingClear: boolean;
  setConfirmingClear: Dispatch<SetStateAction<boolean>>;
  confirmingSkipImages: boolean;
  setConfirmingSkipImages: Dispatch<SetStateAction<boolean>>;
  confirmingAbort: boolean;
  setConfirmingAbort: Dispatch<SetStateAction<boolean>>;
  handleNext: () => Promise<void>;
  handleSkipImagesAndContinue: () => Promise<void>;
  handleStep1Next: () => void;
  handleResumeDraft: () => void;
  handleBack: () => void;
  handleSubmit: () => void;
  handleLoginToCreate: () => void;
  handleClearDraft: () => void;
  handleWalletChange: (walletId: string) => void;
  handleFirstCreationContinue: () => void;
  handleOpenChange: (newOpen: boolean) => void;
  handleConfirmAbort: () => void;
}

/**
 * All of the create-proposal dialog's state, derived flags, navigation /
 * submission handlers, and the open-reset effect.
 */
function useCreateProposalDialog(props: CreateProposalDialogInnerProps): CreateProposalController {
  const {
    open,
    onOpenChange,
    flow,
    setFlow,
    continuation,
    continuationFailure,
    liveEventContext,
    fixtureState
  } = props;
  const t = useTranslations('launchpad.form');
  const tAi = useTranslations('aiProposal');
  // ALLOW-UNKNOWN: next-intl `t` is typed against the static message tree; the resolver dispatches dynamic SDK-emitted keys, so we narrow the function shape (not the value).
  const tRoot = useTranslations() as unknown as DynamicTranslator;
  const store = useProposalFormStore();
  const resetFixtureStore = store.reset;
  const addFixtureOutcome = store.addOutcome;
  const setFixtureStep = store.setStep;
  const updateFixtureField = store.updateField;
  const updateFixtureOutcomeLabel = store.updateOutcomeLabel;
  const updateFixtureOutcomeTicker = store.updateOutcomeTicker;
  const setFixtureOutcomeImage = store.setOutcomeImage;
  const setFixtureCoverImage = store.setCoverImage;
  const {
    toUsdc
  } = useFormattedCurrency();
  const {
    activeWallet,
    setActiveWalletId
  } = useActiveWallet();
  const {
    activeWalletId: canonicalWalletId
  } = useActiveWalletSlot();
  const multiWalletFeature = useMultiWalletFeature();
  const {
    wallets,
    isLoading: walletsLoading,
    error: walletsError
  } = useWallets();
  const eligibleWallets = wallets.filter(wallet => wallet.archivedAt === null && wallet.isAvailable !== false);
  const selectedWallet = eligibleWallets.find(wallet_0 => wallet_0.id === canonicalWalletId);
  const selectedWalletId = selectedWallet?.id ?? null;
  const lastValidFundWalletIdRef = useRef<string | null>(null);
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    openLogin
  } = useAuthGate();
  const turnkeyModalActive = useTurnkeyModalActive();
  const notifyWalletSwitched = useNotifyWalletSwitched();
  const regionEnabled = useFeatureGate('region') === true;
  const walletAddresses = open && store.step === TOTAL_STEPS ? eligibleWallets.map(wallet_1 => wallet_1.address) : [];
  const {
    perWalletRaw: walletBalancesD6,
    isLoading: walletBalancesLoading,
    isError: walletBalancesError
  } = useWalletBalances(walletAddresses);
  const walletDataState = walletsLoading || walletBalancesLoading ? 'loading' : walletsError !== null || walletBalancesError ? 'error' : 'ready';
  const selectedWalletBalanceD6 = selectedWallet === undefined ? undefined : walletBalancesD6.get(selectedWallet.address);
  const feeParams = useCreationFeeParams(open && store.step === TOTAL_STEPS);
  const feeBpsBacking = feeParams.feeBpsBacking;
  const feeParamsState = feeParams.isLoading ? 'loading' : feeParams.isError || feeBpsBacking === undefined ? 'error' : 'ready';
  const walletBalanceState = walletDataState === 'loading' || feeParamsState === 'loading' ? 'loading' : walletDataState === 'error' || feeParamsState === 'error' ? 'error' : 'ready';
  useEffect(() => {
    if (!open || store.step !== TOTAL_STEPS) {
      lastValidFundWalletIdRef.current = null;
      return;
    }
    if (selectedWallet !== undefined) {
      lastValidFundWalletIdRef.current = selectedWallet.id;
    }
  }, [open, selectedWallet, store.step]);
  useEffect(() => {
    if (!open || walletsLoading || walletsError !== null || selectedWallet !== undefined) {
      return;
    }
    const resolvedWallet = activeWallet === null ? undefined : eligibleWallets.find(wallet_2 => wallet_2.id === activeWallet.id);
    if (resolvedWallet === undefined) return;
    const selectedWalletWasRemovedOnFundStep = store.step === TOTAL_STEPS && canonicalWalletId !== null && lastValidFundWalletIdRef.current === canonicalWalletId;
    if (!selectedWalletWasRemovedOnFundStep) setActiveWalletId(resolvedWallet.id);
  }, [activeWallet, canonicalWalletId, eligibleWallets, open, selectedWallet, setActiveWalletId, store.step, walletsError, walletsLoading]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [createdTitle, setCreatedTitle] = useState<string | null>(null);
  const [successMarketPubkey, setSuccessMarketPubkey] = useState<string | null>(null);
  const [confirmingClear, setConfirmingClear] = useState<boolean>(false);
  const [confirmingSkipImages, setConfirmingSkipImages] = useState<boolean>(false);
  const [confirmingAbort, setConfirmingAbort] = useState<boolean>(false);
  const [continuationState, setContinuationState] = useState<{
    active: ProposalAuthContinuation | null;
    resumeMechanism: 'in_place' | 'session_remount';
    fallbackVisible: boolean;
    persistenceLimited: boolean;
  }>(() => ({
    active: continuation ?? null,
    resumeMechanism: continuation === undefined ? 'in_place' : 'session_remount',
    fallbackVisible: continuationFailure !== undefined,
    persistenceLimited: false
  }));
  const {
    active: activeContinuation,
    resumeMechanism,
    fallbackVisible: continuationFallbackVisible,
    persistenceLimited: continuationPersistenceLimited
  } = continuationState;
  const handledContinuationCreatedAtRef = useRef<number | null>(null);
  const loginRequestLockedRef = useRef(false);
  // First-creation onboarding: gated by the device-local flag, captured at
  // success time so `markSeen()` flipping the flag doesn't re-render the
  // panel away mid-view.
  const [onboardingPending, setOnboardingPending] = useState<boolean>(false);
  const markFirstCreationSeen = useProposalFirstCreationStore(s => s.markSeen);

  // Step-1 name + selected method live as dialog-local state until the footer
  // Next commits them (D5/C1). "generate" is the default selection.
  const [name, setName] = useState<string>(() => fixtureState?.name ?? '');
  const [method, setMethod] = useState<'manual' | 'generate'>(() => fixtureState === undefined ? 'generate' : 'manual');
  const sse = useProposalGeneration();
  const aiStreamStatus = useProposalFormStore(s_0 => s_0.aiStreamStatus);
  const aiError = useProposalFormStore(s_1 => s_1.aiError);
  const isAiStreaming = aiStreamStatus === 'streaming';
  const signingDeps = useTurnkeySigningDeps(getExternalWalletProvider);
  const turnkeyDeps = useTurnkeySubmissionDeps();
  const submitTx = useSubmitTransaction();
  const invalidateWalletBalances = useInvalidateWalletBalances();
  const classification = useClassifyProposal();
  const reclassificationAttemptRef = useRef(0);
  const reviewTransitionLockedRef = useRef(false);
  const mutation = useCreateProposalMutation({
    connection: getConnection(),
    submitTransaction: submitTx,
    activeWallet: activeWallet ?? null,
    signingDeps,
    turnkeyDeps
  });

  // Warm the manual image-picker search cache only for the manual flow. The
  // AI generation path uses the Serper-backed Lambda worker instead.
  useImageSearchPrefetch(store.title, {
    enabled: fixtureState === undefined && open && method === 'manual' && store.step <= 2
  });

  // Founding back is OPTIONAL and market-level — `fundAmountD6 === 0` is a valid
  // submission, and there is no outcome to pick (the post-create back is
  // market-level, mirroring the launchpad detail fund form). `store.fundAmount`
  // is the user-typed value in LOCAL display currency, so convert local → USDC
  // → D6 base units to match the wallet-balance scale + the `back(amount)` tx.
  const fundAmountLocal = Number(store.fundAmount) || 0;
  const fundAmountD6 = fundAmountLocal > 0 ? BigInt(usdcToD6(toUsdc(fundAmountLocal)).toString(10)) : BigInt(0);
  const foundingFundQuote = feeBpsBacking === undefined ? null : quoteFoundingFund(fundAmountD6, feeBpsBacking);
  const hasValidFund = foundingFundQuote !== null && selectedWalletBalanceD6 !== undefined && foundingFundQuote.totalD6 <= selectedWalletBalanceD6 && fundAmountD6 <= BigInt(Number.MAX_SAFE_INTEGER);
  const activeWalletMatchesSelection = activeWallet !== null && selectedWallet !== undefined && activeWallet.id === selectedWallet.id && activeWallet.address === selectedWallet.address && activeWallet.type === selectedWallet.type;
  const imagesUploading = store.coverImageUploading || store.outcomes.some(o => o.imageUploading);
  const generatedFlowCreateReady = flow !== 'generate' || store.aiCreateReadyStatus === 'ready';
  const canSubmit = activeWalletMatchesSelection && walletBalanceState === 'ready' && hasValidFund && !submitting && !imagesUploading && generatedFlowCreateReady;
  const authResumePreparing = activeContinuation !== null && (isAuthLoading || isAuthenticated && (!activeWalletMatchesSelection || walletBalanceState !== 'ready'));
  const stepTitles: Record<number, string> = {
    1: t('stepNameTitle'),
    2: t('stepDetailsTitle'),
    3: t('stepPreviewImagesTitle'),
    4: t('stepFundTitle')
  };
  // Per-step description rendered directly below the title (and progress bar)
  // in the modal header — one consistent subtitle slot across all steps.
  const stepDescriptions: Record<number, string> = {
    1: t('stepNameDescription'),
    2: t('stepDetailsDescription'),
    3: t('stepPreviewImagesDescription'),
    4: t('stepFundDescription')
  };

  // The single resumable draft surfaced on step 1 (most-recently-edited of the
  // two per-flow drafts). Read at render — drafts don't mutate while on step 1
  // (the name field is local), so a non-reactive read is correct here.
  const recentDraft = fixtureState === undefined ? selectResumableDraft() : null;

  // FR-022 / US2 AC3 — surface AI hard-errors + network failures via toast.
  // The form itself stays editable (no dead end): once the stream stops,
  // `pending()` is false and every field is editable.
  const isHardError = aiStreamStatus === 'error' && aiError !== null && aiError.retryable === false;
  const isNetworkError = aiStreamStatus === 'error' && aiError !== null && aiError.retryable === true && aiError.code === 'network';
  const isContentPolicyError = aiStreamStatus === 'error' && aiError !== null && aiError.code === 'ai_market_content_policy';
  useEffect(() => {
    if (isNetworkError) {
      showToast({
        id: TOAST_IDS.aiProposalNetworkError,
        message: tAi('errors.networkError'),
        variant: 'error'
      });
      return;
    }
    if (isHardError) {
      showToast({
        id: TOAST_IDS.aiProposalGenerateError,
        message: isContentPolicyError ? tAi('errors.contentPolicy') : tAi('errors.tryDifferentPrompt'),
        variant: 'error'
      });
    }
  }, [isContentPolicyError, isHardError, isNetworkError, tAi]);
  async function advanceToReview(): Promise<void> {
    const attempt = ++reclassificationAttemptRef.current;
    const state = useProposalFormStore.getState();
    if (flow === 'generate') {
      reviewTransitionLockedRef.current = true;
    }
    state.setStep(3);
    if (flow === 'generate') {
      try {
        const result = await classification.mutateAsync({
          title: state.title,
          description: state.description,
          outcomes: state.outcomes.map(outcome => outcome.label)
        });
        const currentState = useProposalFormStore.getState();
        if (attempt === reclassificationAttemptRef.current && currentState.step === 3) {
          currentState.setAiMarketKind(result.kind);
        }
      } catch (error: unknown) {
        logger.warn('proposal_creation.reclassification_failed', {
          errorClass: error instanceof Error ? error.name : typeof error
        });
      }
      if (attempt === reclassificationAttemptRef.current) {
        reviewTransitionLockedRef.current = false;
      }
    }
  }
  async function handleNext(): Promise<void> {
    if (reviewTransitionLockedRef.current) return;
    if (store.step === 2) {
      store.markStepAttempted(2);
      const state_0 = useProposalFormStore.getState();
      const stateWithEventCategory = liveEventContext === undefined ? state_0 : {
        ...state_0,
        category: liveEventContext.category
      };
      const errors = getDetailsStepErrors(stateWithEventCategory, {
        requireLocation: regionEnabled
      });
      if (Object.keys(errors).length > 0) return;
      // Soft, non-blocking nudge for outcome rows missing an image (FR-009).
      if (state_0.outcomes.some(o_0 => o_0.imageUrl === '' && o_0.imageDraft === null)) {
        setConfirmingSkipImages(true);
        return;
      }
      await advanceToReview();
      return;
    }
    if (store.step === 3) {
      store.setStep(4);
    }
  }
  async function handleSkipImagesAndContinue(): Promise<void> {
    setConfirmingSkipImages(false);
    await advanceToReview();
  }

  /** Manual path — seed the entered name as the manual draft's title and
   *  advance to the blank details form (FR-004/FR-023). Writes the manual
   *  store directly (not the active store) so the seed lands regardless of
   *  when the `flow` re-render commits. */
  function chooseManual(trimmed: string): void {
    manualProposalFormStore.getState().updateField('title', trimmed);
    manualProposalFormStore.getState().setStep(2);
    setFlow('manual');
  }

  /** AI path — the entered name IS the generation prompt. Step 1 always runs
   *  with `flow === 'generate'`, so the SSE hook here is bound to the AI store;
   *  `sse.start` resets + streams into it (FR-004/FR-022). */
  function chooseGenerate(trimmed_0: string): void {
    if (isAiStreaming) return;
    void sse.start(trimmed_0);
    aiProposalFormStore.getState().setStep(2);
  }

  /** Footer Next on step 1 — commit the entered name + the selected method.
   *  Disabled (in the footer) until the name is non-empty. */
  function handleStep1Next(): void {
    const trimmed_1 = name.trim();
    if (trimmed_1.length === 0) return;
    if (method === 'manual') chooseManual(trimmed_1);else chooseGenerate(trimmed_1);
  }

  /** Resume the surfaced draft — switch to its flow and jump to the details
   *  form (step 2), where all persisted content is visible + editable (US5). */
  function handleResumeDraft(): void {
    if (recentDraft === null) return;
    const targetStore = recentDraft.flow === 'generate' ? aiProposalFormStore : manualProposalFormStore;
    const targetState = targetStore.getState();
    targetStore.getState().setStep(2);
    setName(recentDraft.title);
    setFlow(recentDraft.flow);
    if (recentDraft.flow === 'generate' && targetState.aiJobId !== null && targetState.aiCreateReadyStatus === 'waiting') {
      window.setTimeout(() => {
        void sse.reconnect();
      }, 0);
    }
  }
  function handleBack(): void {
    // Mid-stream Back: abort the in-flight SSE and return to step 1.
    if (isAiStreaming) {
      sse.cancel();
      store.setStep(1);
      setFlow('generate');
      return;
    }
    // Leaving the details form returns to the name-entry screen, which always
    // runs with flow=generate (so a subsequent Generate binds correctly).
    if (store.step === 2) {
      reclassificationAttemptRef.current += 1;
      store.setStep(1);
      setFlow('generate');
      return;
    }
    if (store.step > 1) {
      if (store.step === 3) {
        reclassificationAttemptRef.current += 1;
        reviewTransitionLockedRef.current = false;
      }
      store.setStep(store.step - 1);
    }
  }
  function clearActiveContinuation(outcome_0: 'created' | 'draft_cleared' | 'dialog_closed'): void {
    if (activeContinuation === null) return;
    clearProposalAuthContinuation();
    onEvent('proposal_auth_continuation_abandoned', {
      flow: activeContinuation.flow,
      sourceStep: 4,
      outcome: outcome_0,
      resumeMechanism,
      elapsedMs: continuationElapsedMs(activeContinuation)
    });
    loginRequestLockedRef.current = false;
    setContinuationState(current => ({
      ...current,
      active: null,
      persistenceLimited: false
    }));
  }
  function handleLoginToCreate(): void {
    if (loginRequestLockedRef.current || store.step !== TOTAL_STEPS) return;
    loginRequestLockedRef.current = true;
    window.setTimeout(() => {
      loginRequestLockedRef.current = false;
    }, 750);
    const nextContinuation = createProposalAuthContinuation(flow, store.lastTouchedAt);
    const persisted = writeProposalAuthContinuation(nextContinuation);
    setContinuationState({
      active: nextContinuation,
      resumeMechanism: 'in_place',
      fallbackVisible: false,
      persistenceLimited: !persisted
    });
    onEvent('proposal_create_login_requested', {
      flow,
      sourceStep: 4,
      resumeMechanism: 'in_place'
    });
    if (!persisted) {
      logger.warn('proposal_creation.auth_resume_failed', {
        reason: 'storage_unavailable'
      });
      onEvent('proposal_auth_continuation_failed', {
        flow,
        sourceStep: 4,
        outcome: 'storage_unavailable',
        resumeMechanism: 'in_place',
        elapsedMs: 0
      });
    }
    openLogin();
  }
  function handleSubmit(): void {
    if (!canSubmit || selectedWallet === undefined || !activeWalletMatchesSelection) return;
    if (selectedWallet.id !== multiWalletFeature.primaryWallet?.id && !multiWalletFeature.canStartOperation('switch', 'proposal_funding')) {
      return;
    }
    setSubmitting(true);
    const titleAtSubmit = store.title;
    const walletAtSubmit = {
      id: selectedWallet.id,
      address: selectedWallet.address,
      type: selectedWallet.type
    } as const;
    const fundAmountAtSubmitD6 = Number(fundAmountD6);
    mutation.mutate({
      title: titleAtSubmit,
      description: store.description,
      // The future event-proposal endpoint owns `event_id` association. Until
      // that contract lands, keep this production mutation unchanged and use
      // the selected event only to lock its existing editorial category.
      category: liveEventContext?.category ?? store.category,
      // The region control can be disabled, but editorial creation requires a location.
      location: store.location.trim() || 'Global',
      ticker: store.marketTicker,
      // ALLOW-NSFW-STRIP: backend-orchestrated metadata — NSFW is DB-only
      coverImageUrl: store.coverImageUrl,
      coverImageDraft: store.coverImageDraft,
      outcomes: store.outcomes.map((o_1, i) => ({
        outcomeIndex: i,
        label: o_1.label,
        ticker: o_1.ticker,
        // ALLOW-NSFW-STRIP: backend-orchestrated metadata — NSFW is DB-only.
        imageUrl: o_1.imageUrl,
        imageDraft: o_1.imageDraft
      })),
      // The success view inside this dialog replaces the toast confirmation.
      silentSuccess: true
    }, {
      wallet: walletAtSubmit,
      onSuccess: (result_0: CreateProposalResult): void => {
        clearActiveContinuation('created');
        const newMarketPubkey = result_0.marketPubkey;
        setSuccessMarketPubkey(newMarketPubkey);
        // Capture the first-creation flag NOW so a later `markSeen()` doesn't
        // tear the onboarding panel down before the user dismisses it (FR-016).
        setOnboardingPending(!useProposalFirstCreationStore.getState().seen);
        if (fundAmountAtSubmitD6 > 0) {
          void (async (): Promise<void> => {
            await runWithCleanup(async () => {
              const traceId = createTransactionTraceId();
              try {
                const {
                  backMarketAndSubmit
                } = await loadBackMarketModule();
                const backResult = await backMarketAndSubmit({
                  submitTx,
                  signingDeps,
                  turnkeyDeps,
                  activeWallet: walletAtSubmit,
                  traceId
                }, {
                  marketPubkey: newMarketPubkey,
                  amountD6: fundAmountAtSubmitD6
                });
                if (backResult.ok) {
                  invalidateWalletBalances();
                } else {
                  showToast({
                    id: TOAST_IDS.PROPOSAL_BACK_ERROR,
                    message: resolveApiErrorMessage(backResult.error, tRoot) ?? t('foundingBackFailed'),
                    variant: 'warning',
                    duration: 8000
                  });
                }
              } catch (err) {
                logger.error('proposal_post_create_back.exception', {
                  traceId,
                  errorClass: err instanceof Error ? 'Error' : typeof err
                });
              }
            }, () => {
              setSubmitting(false);
              setCreatedTitle(titleAtSubmit);
            });
          })();
          return;
        }
        setSubmitting(false);
        setCreatedTitle(titleAtSubmit);
      },
      onError: (): void => {
        setSubmitting(false);
      }
    });
  }
  function handleWalletChange(walletId: string): void {
    if (submitting || walletId === selectedWalletId) return;
    const target = eligibleWallets.find(wallet_3 => wallet_3.id === walletId);
    if (target === undefined) return;
    if (target.id !== multiWalletFeature.primaryWallet?.id && !multiWalletFeature.canStartOperation('switch', 'proposal_funding')) {
      return;
    }
    setActiveWalletId(target.id);
    notifyWalletSwitched({
      fromWalletId: selectedWalletId,
      toWalletId: target.id,
      label: walletDisplayLabel(target)
    });
  }

  /** "Let's go" on the first-creation panel — record the flag, then fall
   *  through to the share screen (FR-017). */
  function handleFirstCreationContinue(): void {
    markFirstCreationSeen();
    setOnboardingPending(false);
  }
  function handleClearDraft(): void {
    clearActiveContinuation('draft_cleared');
    store.reset();
    setConfirmingClear(false);
  }
  const hasDraft = store.title.length > 0 || store.description.length > 0 || store.marketTicker.length > 0 || store.coverImageUrl.length > 0 || store.coverImageDraft !== null || store.outcomes.some(o_2 => o_2.label.length > 0 || o_2.imageDraft !== null);

  // Duplicate outcome labels are a hard block — disable Next on the details
  // step so the user can't advance past them.
  const hasDuplicateOutcomeLabel = ((): boolean => {
    const seen = new Set<string>();
    for (const o_3 of store.outcomes) {
      const normalized = o_3.label.trim().toLowerCase();
      if (normalized.length === 0) continue;
      if (seen.has(normalized)) return true;
      seen.add(normalized);
    }
    return false;
  })();
  function handleOpenChange(newOpen: boolean): void {
    if (submitting) return;
    if (!newOpen && isAiStreaming) {
      setConfirmingAbort(true);
      return;
    }
    if (!newOpen) {
      reclassificationAttemptRef.current += 1;
      sse.cancelSubscriptionOnly();
      clearActiveContinuation('dialog_closed');
    }
    if (!newOpen && createdTitle !== null) {
      setCreatedTitle(null);
      setOnboardingPending(false);
      store.reset();
    }
    onOpenChange(newOpen);
  }
  function handleConfirmAbort(): void {
    setConfirmingAbort(false);
    sse.cancel();
    clearActiveContinuation('dialog_closed');
    onOpenChange(false);
  }

  // Ordinary opens reset only the cursor; persisted field content remains.
  useEffect(() => {
    if (!open || fixtureState !== undefined) return;
    setFlow('generate');
    setName('');
    setMethod('generate');
    aiProposalFormStore.setState({
      step: 1
    });
    manualProposalFormStore.setState({
      step: 1
    });
  }, [fixtureState, open, setFlow]);
  useEffect(() => {
    if (!open || fixtureState === undefined) return;
    resetFixtureStore();
    updateFixtureField('title', fixtureState.draft.title);
    updateFixtureField('marketTicker', fixtureState.draft.marketTicker);
    updateFixtureField('description', fixtureState.draft.description);
    updateFixtureField('location', fixtureState.draft.location);
    updateFixtureField('fundAmount', fixtureState.draft.fundAmount);
    setFixtureCoverImage(fixtureState.draft.coverImageUrl, false);
    for (let index = 2; index < fixtureState.draft.outcomes.length; index += 1) {
      addFixtureOutcome();
    }
    fixtureState.draft.outcomes.forEach((outcome_1, index_0) => {
      updateFixtureOutcomeLabel(index_0, outcome_1.label);
      updateFixtureOutcomeTicker(index_0, outcome_1.ticker);
      setFixtureOutcomeImage(index_0, outcome_1.imageUrl, false);
    });
    setFixtureStep(fixtureState.step);
  }, [addFixtureOutcome, fixtureState, open, resetFixtureStore, setFixtureCoverImage, setFixtureOutcomeImage, setFixtureStep, updateFixtureField, updateFixtureOutcomeLabel, updateFixtureOutcomeTicker]);

  // A validated auth continuation is the only path that may bypass the normal
  // step-1 cursor reset after hydration.
  useEffect(() => {
    if (!open || continuation === undefined) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      if (handledContinuationCreatedAtRef.current === continuation.createdAt) return;
      handledContinuationCreatedAtRef.current = continuation.createdAt;
      const targetStore_0 = continuation.flow === 'generate' ? aiProposalFormStore : manualProposalFormStore;
      const targetState_0 = targetStore_0.getState();
      const failureReason = validateProposalAuthContinuationDraft(continuation, targetState_0, regionEnabled);
      if (failureReason !== null) {
        clearProposalAuthContinuation();
        logger.warn('proposal_creation.auth_resume_failed', {
          reason: failureReason
        });
        onEvent('proposal_auth_continuation_failed', {
          flow: continuation.flow,
          sourceStep: 4,
          outcome: failureReason,
          resumeMechanism: 'session_remount',
          elapsedMs: Math.max(0, Date.now() - continuation.createdAt)
        });
        setContinuationState({
          active: null,
          resumeMechanism: 'session_remount',
          fallbackVisible: true,
          persistenceLimited: false
        });
        return;
      }
      setFlow(continuation.flow);
      targetStore_0.setState({
        step: TOTAL_STEPS,
        ...(continuation.flow === 'generate' ? {
          aiCreateReadyStatus: 'ready' as const
        } : {})
      });
      setContinuationState({
        active: continuation,
        resumeMechanism: 'session_remount',
        fallbackVisible: false,
        persistenceLimited: false
      });
      onEvent('proposal_auth_continuation_restored', {
        flow: continuation.flow,
        sourceStep: 4,
        outcome: 'restored',
        resumeMechanism: 'session_remount',
        elapsedMs: Math.max(0, Date.now() - continuation.createdAt)
      });
    });
    return (): void => window.cancelAnimationFrame(frameId);
  }, [continuation, open, regionEnabled, setFlow]);
  useEffect(() => {
    if (activeContinuation === null) return undefined;
    const remainingMs = activeContinuation.expiresAt - Date.now();
    const expire = (): void => {
      clearProposalAuthContinuation();
      logger.warn('proposal_creation.auth_resume_failed', {
        reason: 'expired'
      });
      onEvent('proposal_auth_continuation_failed', {
        flow: activeContinuation.flow,
        sourceStep: 4,
        outcome: 'expired',
        resumeMechanism,
        elapsedMs: Math.max(0, Date.now() - activeContinuation.createdAt)
      });
      setContinuationState({
        active: null,
        resumeMechanism,
        fallbackVisible: true,
        persistenceLimited: false
      });
      setFlow('generate');
      aiProposalFormStore.setState({
        step: 1
      });
      manualProposalFormStore.setState({
        step: 1
      });
    };
    const timeoutId = window.setTimeout(expire, Math.max(0, remainingMs));
    return (): void => window.clearTimeout(timeoutId);
  }, [activeContinuation, resumeMechanism, setFlow]);
  const restoredInPlaceCreatedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (activeContinuation === null || resumeMechanism !== 'in_place' || !isAuthenticated || isAuthLoading || restoredInPlaceCreatedAtRef.current === activeContinuation.createdAt) {
      return;
    }
    restoredInPlaceCreatedAtRef.current = activeContinuation.createdAt;
    onEvent('proposal_auth_continuation_restored', {
      flow: activeContinuation.flow,
      sourceStep: 4,
      outcome: 'restored',
      resumeMechanism: 'in_place',
      elapsedMs: Math.max(0, Date.now() - activeContinuation.createdAt)
    });
  }, [activeContinuation, isAuthenticated, isAuthLoading, resumeMechanism]);
  const createCtaReady = activeContinuation !== null && isAuthenticated && !authResumePreparing && canSubmit;
  const previousCreateCtaReadyRef = useRef(false);
  useEffect(() => {
    if (createCtaReady && !previousCreateCtaReadyRef.current) {
      window.requestAnimationFrame(() => {
        document.getElementById('proposal-create-after-auth')?.focus();
      });
    }
    previousCreateCtaReadyRef.current = createCtaReady;
  }, [createCtaReady]);
  const previousTurnkeyModalActiveRef = useRef(turnkeyModalActive);
  useEffect(() => {
    const authModalClosed = previousTurnkeyModalActiveRef.current && !turnkeyModalActive;
    previousTurnkeyModalActiveRef.current = turnkeyModalActive;
    if (!authModalClosed || activeContinuation === null || isAuthenticated || isAuthLoading) {
      return undefined;
    }
    const frameId_0 = window.requestAnimationFrame(() => {
      document.getElementById('proposal-login-to-create')?.focus();
    });
    return (): void => window.cancelAnimationFrame(frameId_0);
  }, [activeContinuation, isAuthenticated, isAuthLoading, turnkeyModalActive]);
  const activeContinuationRef = useRef(activeContinuation);
  const turnkeyModalActiveRef = useRef(turnkeyModalActive);
  const resumeMechanismRef = useRef(resumeMechanism);
  useEffect(() => {
    activeContinuationRef.current = activeContinuation;
    turnkeyModalActiveRef.current = turnkeyModalActive;
    resumeMechanismRef.current = resumeMechanism;
  }, [activeContinuation, resumeMechanism, turnkeyModalActive]);
  useEffect(() => (): void => {
    const pending = activeContinuationRef.current;
    if (pending === null || turnkeyModalActiveRef.current) return;
    clearProposalAuthContinuation();
    onEvent('proposal_auth_continuation_abandoned', {
      flow: pending.flow,
      sourceStep: 4,
      outcome: 'dialog_closed',
      resumeMechanism: resumeMechanismRef.current,
      elapsedMs: Math.max(0, Date.now() - pending.createdAt)
    });
  }, []);
  const showSuccess = createdTitle !== null;
  const showFirstCreationPanel = showSuccess && onboardingPending;
  const successPubkey = successMarketPubkey;
  const previousShowSuccessRef = useRef(false);
  useEffect(() => {
    if (showSuccess && !previousShowSuccessRef.current) {
      void fireMarketCreatedBurst();
    }
    previousShowSuccessRef.current = showSuccess;
  }, [showSuccess]);
  const successDialogTitle = t('success.title');
  return {
    t,
    tAi,
    store,
    open,
    onOpenChange,
    flow,
    liveEventContext,
    name,
    setName,
    method,
    setMethod,
    recentDraft,
    locationEnabled: regionEnabled,
    submitting,
    reviewTransitionPending: classification.isPending,
    createdTitle,
    isAiStreaming,
    eligibleWallets,
    selectedWalletId,
    walletBalancesD6,
    walletBalanceState,
    feeBpsBacking,
    feeBpsBuy: feeParams.feeBpsBuy,
    feeBpsSellNormal: feeParams.feeBpsSellNormal,
    feeSplitBackersBps: feeParams.feeSplitBackersBps,
    creationThresholdD6: feeParams.creationThresholdD6,
    configStatus: feeParams.configStatus,
    canSubmit,
    stepTitles,
    stepDescriptions,
    hasDraft,
    hasDuplicateOutcomeLabel,
    showSuccess,
    showFirstCreationPanel,
    successPubkey,
    successDialogTitle,
    mutationProgress: mutation.progress,
    isAuthenticated,
    authResumePreparing,
    continuationFallbackVisible,
    continuationPersistenceLimited,
    fixtureValidation: fixtureState?.validation,
    confirmingClear,
    setConfirmingClear,
    confirmingSkipImages,
    setConfirmingSkipImages,
    confirmingAbort,
    setConfirmingAbort,
    handleNext,
    handleSkipImagesAndContinue,
    handleStep1Next,
    handleResumeDraft,
    handleBack,
    handleSubmit,
    handleLoginToCreate,
    handleClearDraft,
    handleWalletChange,
    handleFirstCreationContinue,
    handleOpenChange,
    handleConfirmAbort
  };
}
function CreateProposalDialogInner(props) {
  const $ = _c(31);
  const c = useCreateProposalDialog(props);
  const {
    t,
    store
  } = c;
  const successHeaderActive = c.showSuccess && c.successPubkey !== null && c.createdTitle !== null;
  let t0;
  if ($[0] !== c) {
    t0 = () => c.setConfirmingAbort(true);
    $[0] = c;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = c.showSuccess ? c.createdTitle ?? c.successDialogTitle : c.stepTitles[store.step] ?? "";
  const t2 = c.showSuccess ? undefined : c.stepDescriptions[store.step] ?? "";
  const t3 = !c.showFirstCreationPanel;
  let t4;
  if ($[2] !== c) {
    t4 = c.showSuccess ? undefined : <CreateProposalFooter c={c} />;
    $[2] = c;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== c.showSuccess || $[5] !== c.stepDescriptions || $[6] !== c.stepTitles || $[7] !== store) {
    t5 = c.showSuccess ? undefined : <div className="flex flex-col gap-2"><span className="text-heading-md">{c.stepTitles[store.step] ?? ""}</span>{(c.stepDescriptions[store.step] ?? "") !== "" && <p className="text-body text-foreground-secondary">{c.stepDescriptions[store.step]}</p>}</div>;
    $[4] = c.showSuccess;
    $[5] = c.stepDescriptions;
    $[6] = c.stepTitles;
    $[7] = store;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== c || $[10] !== store || $[11] !== successHeaderActive || $[12] !== t) {
    t6 = c.showFirstCreationPanel && c.createdTitle !== null ? <ProposalFirstCreationPanel title={c.createdTitle} onContinue={c.handleFirstCreationContinue} /> : successHeaderActive && c.successPubkey !== null && c.createdTitle !== null ? <ProposalCreatedSuccess proposalPubkey={c.successPubkey} title={c.createdTitle} onViewMarket={() => c.handleOpenChange(false)} /> : c.submitting && c.mutationProgress.status !== "idle" ? <div className="flex flex-col gap-3"><CreateMarketProgressStrip progress={c.mutationProgress} /></div> : <MotionAnimatedAutoHeight layoutDependencies={[store.step, c.submitting]} innerClassName="flex flex-col gap-4">{c.continuationFallbackVisible && store.step === 1 && <StatusBanner tone="warning" title={t("authResumeStaleTitle")} description={t("authResumeStaleBody")} />}{c.liveEventContext !== undefined && <EventTimeCard variant="proposal" {...c.liveEventContext} />}{c.fixtureValidation !== undefined && <div data-testid="proposal-fixture-validation" data-validation-kind={c.fixtureValidation.kind}><StatusBanner tone="destructive" title={c.fixtureValidation.title} description={<>{c.fixtureValidation.description}{c.fixtureValidation.action !== undefined ? <>{" "}<a href={c.fixtureValidation.action.href} className="font-medium underline underline-offset-2">{c.fixtureValidation.action.label}</a></> : null}</>} /></div>}{c.continuationPersistenceLimited && store.step === TOTAL_STEPS && <StatusBanner tone="warning" title={t("authResumeSessionOnlyTitle")} description={t("authResumeSessionOnlyBody")} />}{store.step === 2 && store.outcomes.some(_temp14) && <StatusBanner tone="info" title={t("outcomeImagesHint")} description={t("outcomeImagesHintBody")} />}{store.step === 3 && store.aiMarketKind === "prediction" && <StatusBanner tone="warning" title={t("predictionStyleWarningTitle")} description={t("predictionStyleWarningBody", {
        exampleA: t("predictionStyleWarningExampleA"),
        exampleB: t("predictionStyleWarningExampleB")
      })} />}<div>{store.step === 1 && <ProposalStepNameEntry name={c.name} onNameChange={c.setName} method={c.method} onMethodChange={c.setMethod} onSubmit={c.handleStep1Next} recentDraft={c.recentDraft} onResumeDraft={c.handleResumeDraft} />}{store.step === 2 && <ProposalDetailsForm locationEnabled={c.locationEnabled} lockedCategory={c.liveEventContext?.category} />}{store.step === 3 && <CreateProposalStepPreview categoryOverride={c.liveEventContext?.category} />}{store.step === 4 && <CreateProposalStep3 eligibleWallets={c.eligibleWallets} selectedWalletId={c.selectedWalletId} walletBalancesD6={c.walletBalancesD6} walletBalanceState={c.walletBalanceState} onWalletChange={c.handleWalletChange} submitting={c.submitting} feeBpsBacking={c.feeBpsBacking} feeBpsBuy={c.feeBpsBuy} feeBpsSellNormal={c.feeBpsSellNormal} feeSplitBackersBps={c.feeSplitBackersBps} creationThresholdD6={c.creationThresholdD6} configStatus={c.configStatus} />}</div></MotionAnimatedAutoHeight>;
    $[9] = c;
    $[10] = store;
    $[11] = successHeaderActive;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== c.handleOpenChange || $[15] !== c.isAiStreaming || $[16] !== c.open || $[17] !== c.showFirstCreationPanel || $[18] !== t0 || $[19] !== t1 || $[20] !== t2 || $[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <SilkSheet open={c.open} onOpenChange={c.handleOpenChange} variant="page" size="lg" surface="primary" disableDesktopSwipe={true} guardDismiss={c.isAiStreaming} onDismissAttempt={t0} title={t1} description={t2} srOnlyHeader={c.showFirstCreationPanel} closeButton={t3} footer={t4} header={t5}>{t6}</SilkSheet>;
    $[14] = c.handleOpenChange;
    $[15] = c.isAiStreaming;
    $[16] = c.open;
    $[17] = c.showFirstCreationPanel;
    $[18] = t0;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== c) {
    t8 = <CreateProposalConfirmations c={c} />;
    $[26] = c;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t7 || $[29] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}

/** Footer: Back (left) · step indicator (centered, width-capped) · Next |
 *  Submit | Login (right). The draft-saved pill sits on a centered row below
 *  when a draft exists. Step 1's Next commits the chosen creation method. */
function _temp14(o) {
  return o.imageUrl === "" && o.imageDraft === null;
}
function CreateProposalFooter(t0) {
  const $ = _c(47);
  const {
    c
  } = t0;
  const {
    t,
    store
  } = c;
  if (c.showSuccess) {
    return null;
  }
  let t1;
  if ($[0] !== c.handleBack || $[1] !== c.submitting || $[2] !== store.step || $[3] !== t) {
    t1 = store.step > 1 && <AnimatedButton type="button" variant="secondary" onClick={c.handleBack} disabled={c.submitting}>{t("back")}</AnimatedButton>;
    $[0] = c.handleBack;
    $[1] = c.submitting;
    $[2] = store.step;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex flex-1 justify-start">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = store.step - 1;
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="flex w-28 shrink-0 justify-center"><ProgressDots currentStep={t3} totalSteps={TOTAL_STEPS} /></div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== c.fixtureValidation?.blocksProgress || $[10] !== c.handleStep1Next || $[11] !== c.name || $[12] !== store.step || $[13] !== t) {
    t5 = store.step === 1 && <AnimatedButton type="button" variant="primary" onClick={c.handleStep1Next} disabled={c.name.trim().length === 0 || c.fixtureValidation?.blocksProgress === true}>{t("next")}</AnimatedButton>;
    $[9] = c.fixtureValidation?.blocksProgress;
    $[10] = c.handleStep1Next;
    $[11] = c.name;
    $[12] = store.step;
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== c.handleNext || $[16] !== c.hasDuplicateOutcomeLabel || $[17] !== c.isAiStreaming || $[18] !== c.reviewTransitionPending || $[19] !== c.submitting || $[20] !== store.step || $[21] !== store.title || $[22] !== t) {
    t6 = (store.step === 2 || store.step === 3) && <AnimatedButton type="button" variant="primary" onClick={c.handleNext} disabled={c.submitting || c.reviewTransitionPending || store.step === 2 && (c.isAiStreaming || c.hasDuplicateOutcomeLabel)}>{store.step === 2 && !c.isAiStreaming && detectPredictionStyle(store.title) ? <Icon icon={Alert01IconSolid} className="size-4 text-feedback-warning-content" aria-hidden="true" /> : null}{t("next")}</AnimatedButton>;
    $[15] = c.handleNext;
    $[16] = c.hasDuplicateOutcomeLabel;
    $[17] = c.isAiStreaming;
    $[18] = c.reviewTransitionPending;
    $[19] = c.submitting;
    $[20] = store.step;
    $[21] = store.title;
    $[22] = t;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== c.authResumePreparing || $[25] !== c.canSubmit || $[26] !== c.handleLoginToCreate || $[27] !== c.handleSubmit || $[28] !== c.isAuthenticated || $[29] !== c.submitting || $[30] !== store.step || $[31] !== t) {
    t7 = store.step === TOTAL_STEPS && (c.authResumePreparing ? <output aria-live="polite"><AnimatedButton type="button" disabled={true} loading={true}>{t("preparingAfterLogin")}</AnimatedButton></output> : !c.isAuthenticated ? <AnimatedButton type="button" id="proposal-login-to-create" onClick={c.handleLoginToCreate}>{t("loginToCreate")}</AnimatedButton> : <AnimatedButton id="proposal-create-after-auth" type="button" onClick={c.handleSubmit} disabled={!c.canSubmit} loading={c.submitting}>{c.submitting ? t("submitting") : t("submit")}</AnimatedButton>);
    $[24] = c.authResumePreparing;
    $[25] = c.canSubmit;
    $[26] = c.handleLoginToCreate;
    $[27] = c.handleSubmit;
    $[28] = c.isAuthenticated;
    $[29] = c.submitting;
    $[30] = store.step;
    $[31] = t;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t5 || $[34] !== t6 || $[35] !== t7) {
    t8 = <div className="flex flex-1 justify-end">{t5}{t6}{t7}</div>;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t2 || $[38] !== t4 || $[39] !== t8) {
    t9 = <div className="flex items-center gap-3">{t2}{t4}{t8}</div>;
    $[37] = t2;
    $[38] = t4;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== c || $[42] !== store.step) {
    t10 = c.hasDraft && store.step > 1 && <div className="flex justify-center"><DraftSavedPill onClearDraft={() => c.setConfirmingClear(true)} /></div>;
    $[41] = c;
    $[42] = store.step;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== t10 || $[45] !== t9) {
    t11 = <div className="flex flex-col gap-2">{t9}{t10}</div>;
    $[44] = t10;
    $[45] = t9;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  return t11;
}

/**
 * The three confirm sheets (skip-images nudge, clear-draft, abort-AI). Each is
 * conditionally MOUNTED — not just toggled via `open` — because stacking a
 * persistently-mounted SilkSheet on top of the already-open main sheet leaves a
 * stale Silk overlay after a dismiss.
 */
function CreateProposalConfirmations(t0) {
  const $ = _c(13);
  const {
    c
  } = t0;
  const {
    t,
    tAi
  } = c;
  let t1;
  if ($[0] !== c || $[1] !== t) {
    t1 = c.confirmingSkipImages && <SilkSheet open={true} onOpenChange={c.setConfirmingSkipImages} variant="detached" size="sm" title={t("outcomeImagesNudgeTitle")} description={t("outcomeImagesNudgeBody")} footer={<div className="flex justify-end gap-2"><AnimatedButton type="button" variant="secondary" onClick={c.handleSkipImagesAndContinue}>{t("outcomeImagesNudgeSkip")}</AnimatedButton><AnimatedButton type="button" onClick={() => c.setConfirmingSkipImages(false)}>{t("outcomeImagesNudgeAdd")}</AnimatedButton></div>} />;
    $[0] = c;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== c || $[4] !== t) {
    t2 = c.confirmingClear && <SilkSheet open={true} onOpenChange={c.setConfirmingClear} variant="detached" size="sm" title={t("clearDraftConfirmTitle")} description={t("clearDraftConfirmBody")} footer={<div className="flex justify-end gap-2"><AnimatedButton type="button" variant="secondary" onClick={() => c.setConfirmingClear(false)}>{t("cancel")}</AnimatedButton><AnimatedButton type="button" variant="destructive" onClick={c.handleClearDraft}>{t("clearDraftConfirmAction")}</AnimatedButton></div>} />;
    $[3] = c;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== c || $[7] !== tAi) {
    t3 = c.confirmingAbort && <SilkSheet open={true} onOpenChange={c.setConfirmingAbort} variant="detached" size="sm" title={tAi("abortConfirm.title")} description={tAi("abortConfirm.body")} footer={<div className="flex justify-end gap-2"><AnimatedButton type="button" variant="secondary" onClick={() => c.setConfirmingAbort(false)}>{tAi("abortConfirm.cancel")}</AnimatedButton><AnimatedButton type="button" variant="destructive" onClick={c.handleConfirmAbort}>{tAi("abortConfirm.confirm")}</AnimatedButton></div>} />;
    $[6] = c;
    $[7] = tAi;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}